/**
 * Template auto-selection for sidebar dictation.
 *
 * Two STT display modes (see getSidebarDictationSttMode):
 * - incremental (en, es, …): sidebar textarea is authoritative; gate on sidebar word count.
 * - soniox (he, fr, ar): sidebar syncs from cumulative after each final; gate on session
 *   dictation text (anchored Soniox cumulative) so template checks see speech before UI sync.
 */

import { logSidebarTemplate, previewText } from "./sidebarTemplateSelectionDebug";

export const DEFAULT_TEMPLATE_SELECTION_MAX_WORDS = 8;

export const STT_MODE = {
  INCREMENTAL: "incremental",
  SONIOX: "soniox",
};

export function countWords(text) {
  const normalized = String(text ?? "").trim();
  if (!normalized) return 0;
  return normalized.split(/\s+/).filter(Boolean).length;
}

export function takeFirstWords(text, maxWords) {
  const words = String(text ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length || maxWords <= 0) return "";
  return words.slice(0, maxWords).join(" ");
}

function sliceAfterAnchor(fullCumulative, anchor) {
  const cumulative = String(fullCumulative ?? "");
  const anchorText = String(anchor ?? "");
  if (!anchorText) return cumulative;
  if (cumulative.startsWith(anchorText)) {
    return cumulative.slice(anchorText.length).replace(/^\s+/, "");
  }
  return cumulative;
}

export class SidebarTemplateSelectionSession {
  constructor({ maxWords = DEFAULT_TEMPLATE_SELECTION_MAX_WORDS } = {}) {
    this.maxWords = maxWords;
    this.sttMode = STT_MODE.INCREMENTAL;
    this.lockedOut = false;
    this.sidebarText = "";
    this.rawCumulative = "";
    this.anchor = "";
    this.sessionText = "";
    this.pendingPostClearBaseline = false;
    /** Soniox: session word count before the latest cumulative ingest. */
    this.previousSessionWordCount = 0;
  }

  resetForMicSession({ sttMode = STT_MODE.INCREMENTAL } = {}) {
    this.sttMode = sttMode;
    this.lockedOut = false;
    this.sidebarText = "";
    this.rawCumulative = "";
    this.anchor = "";
    this.sessionText = "";
    this.pendingPostClearBaseline = false;
    this.previousSessionWordCount = 0;
    logSidebarTemplate("mic-session-start", { sttMode, maxWords: this.maxWords });
  }

  onTranscriptCleared() {
    this.pendingPostClearBaseline = true;
    this.anchor = "";
    this.sessionText = "";
    this.sidebarText = "";
    this.lockedOut = false;
    this.previousSessionWordCount = 0;
    logSidebarTemplate("transcript-cleared", {
      sttMode: this.sttMode,
      awaitingPostClearBaseline: true,
    });
  }

  updateSidebarText(text) {
    this.sidebarText = String(text ?? "");
    if (this.sttMode === STT_MODE.INCREMENTAL) {
      this._updateLockout(this.sidebarText, { source: "sidebar-update" });
    }
  }

  ingestRawCumulative(rawCumulative) {
    const raw = String(rawCumulative ?? "");
    if (!raw) {
      return this.getSonioxSnapshot();
    }

    this.rawCumulative = raw;

    if (this.pendingPostClearBaseline) {
      this.anchor = raw;
      this.pendingPostClearBaseline = false;
      this.sessionText = "";
      logSidebarTemplate("post-clear-baseline", {
        anchorLength: this.anchor.length,
        anchorPreview: previewText(this.anchor),
      });
    } else {
      this.sessionText = sliceAfterAnchor(raw, this.anchor);
    }

    logSidebarTemplate("cumulative-ingested", {
      sttMode: this.sttMode,
      rawLength: raw.length,
      sessionWordCount: countWords(this.sessionText),
      sessionPreview: previewText(this.sessionText),
      anchorLength: this.anchor.length,
    });

    return this.getSonioxSnapshot();
  }

  /**
   * @param {{
   *   commandMode: boolean,
   *   sidebarText?: string,
   *   sessionText?: string,
   *   pendingDelta?: string,
   * }} options
   */
  getVoiceTemplateGate({
    commandMode,
    sidebarText,
    sessionText,
    pendingDelta = "",
  }) {
    const visible = String(sidebarText ?? this.sidebarText);
    const session = String(sessionText ?? this.sessionText);
    const delta = String(pendingDelta ?? "").trim();

    const gatingText =
      this.sttMode === STT_MODE.SONIOX ? session : visible;
    const sessionWordCount = countWords(session);
    const gatingWordCount =
      this.sttMode === STT_MODE.SONIOX ? sessionWordCount : countWords(gatingText);

    const crossingThreshold =
      this.sttMode === STT_MODE.SONIOX &&
      this.previousSessionWordCount <= this.maxWords &&
      sessionWordCount > this.maxWords;

    const allowedInDictation =
      !this.lockedOut &&
      (this.sttMode === STT_MODE.SONIOX
        ? sessionWordCount <= this.maxWords || crossingThreshold
        : gatingWordCount <= this.maxWords);

    const matchSource =
      this.sttMode === STT_MODE.SONIOX
        ? session
        : [visible, delta]
            .map((part) => String(part ?? "").trim())
            .filter(Boolean)
            .join(" ");

    const gate = {
      sttMode: this.sttMode,
      gatingWordCount,
      sidebarWordCount: countWords(visible),
      sessionWordCount,
      previousSessionWordCount: this.previousSessionWordCount,
      crossingThreshold,
      lockedOut: this.lockedOut,
      allowTemplateCommands: commandMode || allowedInDictation,
      allowTriggerlessTemplate: commandMode ? false : allowedInDictation,
      templateMatchText: allowedInDictation
        ? takeFirstWords(matchSource, this.maxWords)
        : "",
    };

    logSidebarTemplate("template-gate", {
      commandMode,
      ...gate,
      gatingSource: this.sttMode === STT_MODE.SONIOX ? "session" : "sidebar",
      sidebarPreview: previewText(visible),
      sessionPreview: previewText(session),
      deltaPreview: previewText(delta),
      matchPreview: previewText(gate.templateMatchText),
    });

    return gate;
  }

  /** Prevent further auto-selection until sidebar clear or new mic session. */
  lockAfterTemplateSelected() {
    if (this.lockedOut) return;
    this.lockedOut = true;
    logSidebarTemplate("lockout-changed", {
      source: "template-selected",
      sttMode: this.sttMode,
      prevLockedOut: false,
      lockedOut: true,
      wordCount: countWords(
        this.sttMode === STT_MODE.SONIOX ? this.sessionText : this.sidebarText,
      ),
      maxWords: this.maxWords,
    });
  }

  /** Run after template voice engine — lockout must not run before matching (Soniox). */
  finalizeAfterTemplateAttempt() {
    if (this.sttMode === STT_MODE.SONIOX) {
      this._updateLockout(this.sessionText, { source: "soniox-after-template-check" });
      this.previousSessionWordCount = countWords(this.sessionText);
      return;
    }
    this.previousSessionWordCount = 0;
  }

  getSonioxSnapshot() {
    return {
      sessionText: this.sessionText,
      sessionWordCount: countWords(this.sessionText),
    };
  }

  _updateLockout(text, { source } = {}) {
    const prevLockedOut = this.lockedOut;
    const wordCount = countWords(text);
    if (wordCount === 0) {
      this.lockedOut = false;
    } else if (!this.lockedOut && wordCount > this.maxWords) {
      this.lockedOut = true;
    }
    if (prevLockedOut !== this.lockedOut) {
      logSidebarTemplate("lockout-changed", {
        source,
        sttMode: this.sttMode,
        prevLockedOut,
        lockedOut: this.lockedOut,
        wordCount,
        maxWords: this.maxWords,
        textPreview: previewText(text),
      });
    }
  }
}

export function createSidebarTemplateSelectionSession(options) {
  return new SidebarTemplateSelectionSession(options);
}
